type StageWithTitles = {
  _id: string;
  stage_name: string;
  slug?: string;
  listing_type?: string;
  selected?: boolean;
  display_titles?: Array<{
    _id: string;
    name: string;
    selected?: boolean;
  }>;
};

const normalizeText = (value?: string | null) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");

export const getSelectedStageDisplayTitle = ({
  stages,
  stageSlug,
  stageName,
  fallbackLabel,
  uppercase = true,
}: {
  stages: StageWithTitles[];
  stageSlug?: string | null;
  stageName?: string | null;
  fallbackLabel?: string;
  uppercase?: boolean;
}): string => {
  const fallback = fallbackLabel || stageName || "";

  if (!Array.isArray(stages) || stages.length === 0) {
    return uppercase ? fallback.toUpperCase() : fallback;
  }

  const nSlug = normalizeText(stageSlug);
  const nName = normalizeText(stageName);
  const nFallback = normalizeText(fallbackLabel);

  const matchedStage =
    // 1) exact slug match
    stages.find((s) => normalizeText(s.slug) === nSlug) ||
    // 2) loose slug match (eg: "under-offer" matches "under-offer-contract")
    stages.find((s) => {
      const sSlug = normalizeText(s.slug);
      return (
        !!nSlug &&
        !!sSlug &&
        (sSlug.includes(nSlug) || nSlug.includes(sSlug))
      );
    }) ||
    // 3) stage_name match (special characters ignore karke)
    stages.find((s) => {
      const sName = normalizeText(s.stage_name);
      return (
        !!sName &&
        (sName === nName ||
          sName === nFallback ||
          (nName && (sName.includes(nName) || nName.includes(sName))) ||
          (nFallback &&
            (sName.includes(nFallback) || nFallback.includes(sName))))
      );
    });

  if (!matchedStage) {
    return uppercase ? fallback.toUpperCase() : fallback;
  }

  // Selected display title priority
  const selectedTitle = matchedStage.display_titles?.find((t) => t.selected);

  const label =
    selectedTitle?.name || fallback || matchedStage.stage_name || "";

  return uppercase ? label.toUpperCase() : label;
};

export const mapDesignStageToPropertyStageSlug = (
  designStage?: string,
): string | null => {
  if (!designStage) return null;

  const explicitMap: Record<string, string> = {
    coming_soon_property_marketing_for_sale: "coming-soon",
    coming_soon_property_marketing_for_rent: "coming-soon",

    for_sale_listing_property_marketing_for_sale: "for-sale",
    for_rent_listing_property_marketing_for_rent: "for-rent",

    auction_property_marketing_for_sale: "auction",

    under_offer_property_marketing_for_sale: "under-offer",

    open_home_property_marketing_for_sale: "open-home",
    open_home_property_marketing_for_rent: "open-home",

    deposit_taken_property_marketing_for_rent: "deposit-taken",

    sold_agent_marketing_for_sale: "sold",
    leased_agent_marketing_for_rent: "leased",

    review_post_agent_marketing_for_sale: "review",
    review_post_agent_marketing_for_rent: "review",

    review_recap_agent_marketing_for_sale: "review-recap",
    review_recap_agent_marketing_for_rent: "review-recap",

    under_offer_recap_agent_marketing_for_sale: "under-offer-recap",
    sold_recap_agent_marketing_for_sale: "sold-recap",
    leased_recap_agent_marketing_for_rent: "leased-recap",
  };

  if (explicitMap[designStage]) {
    return explicitMap[designStage];
  }

  const base = designStage
    .replace(/_property_marketing_for_sale$/, "")
    .replace(/_property_marketing_for_rent$/, "")
    .replace(/_agent_marketing_for_sale$/, "")
    .replace(/_agent_marketing_for_rent$/, "");

  return base.replace(/_/g, "-");
};

export const getListingTypeFromDesignStage = (
  designStage?: string,
): "sell" | "rent" => {
  if (!designStage) return "sell";
  if (designStage.includes("for_rent") || designStage.includes("rent")) {
    return "rent";
  }
  return "sell";
};

// 🔒 Global cache to prevent duplicate API requests across cards
const stagesCache: Record<string, { promise?: Promise<any[]>; data?: any[] }> =
  {};

export const fetchStagesWithTitlesOnce = async (
  listingType: "sell" | "rent",
  fetcher: (listingType: "sell" | "rent") => Promise<any>,
): Promise<any[]> => {
  const key = listingType;

  if (stagesCache[key]?.data) {
    return stagesCache[key].data as any[];
  }

  if (stagesCache[key]?.promise) {
    return stagesCache[key].promise as Promise<any[]>;
  }

  stagesCache[key] = {
    promise: (async () => {
      const res = await fetcher(listingType);
      const data =
        res?.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
      stagesCache[key].data = data;
      return data;
    })(),
  };

  try {
    return await (stagesCache[key].promise as Promise<any[]>);
  } catch (e) {
    delete stagesCache[key];
    throw e;
  }
};
